import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MarkdownRenderer from '../MicroLearning/MarkdownRenderer';
import TranslateAmharicButton from '../MicroLearning/TranslateAmharicButton';
import QuizCanvas, { ExamAccent } from '../ExamRun/QuizCanvas';
import ContentFigure from '../MicroLearning/ContentFigure';
import PassagePanel from '../PassagePanel';
import { MockPaper, gamificationAPI, packagesAPI, pastExamAPI, track } from '../../services/api';
import './PackageSitting.css';

type Victory = Awaited<ReturnType<typeof gamificationAPI.sessionSummary>>;

/** Strip markdown so a truncated one-line summary never shows a dangling `**`.
 *  Slicing raw markdown at 110 characters cuts mid-emphasis and the leftover
 *  asterisks render literally — which is what the review list was doing. */
const plain = (md: string) =>
  (md || '')
    .replace(/\$\$?[^$]*\$\$?/g, '…')
    .replace(/[*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * The sitting — a timed paper with sequential, independently-clocked sections.
 *
 * ## Why this is a new surface and not a fourth thing that looks like the other
 * three
 *
 * Two timed runners already exist and both were read before this was written:
 *
 *   weekly_mock_router + WeeklyMock.tsx   a full timed paper, subject-
 *                                         parameterised, with a countdown that
 *                                         is explicitly ADVISORY — it does not
 *                                         close anything at zero
 *   LiveExam.tsx                          a hard deadline with auto-submit, but
 *                                         not subject-parameterised
 *
 * Neither has the part that is actually specific to this exam: SECTIONS THAT
 * RUN IN ORDER AND CLOSE ON THEIR OWN CLOCK. 55 verbal in 60 minutes, then —
 * whatever you have or have not finished — quantitative opens and verbal is
 * gone. That lock is the format-shock inoculation, it is the only genuinely new
 * behaviour here, and it is why this could not be a flag on WeeklyMock: putting
 * a hard auto-close into a surface live ESSLCE students rely on mid-season is
 * how you get a bad week.
 *
 * What IS borrowed: `QuizCanvas`, the same question surface the aptitude drill,
 * Daily Focus and Weekly Mock all render — so markdown, LaTeX, media, the
 * progress rail and the Amharic button arrive for free and look identical to
 * every other question in Lomi. It gained an `examMode` prop rather than a
 * fork.
 *
 * ## Practice reveals, an exam does not
 *
 * Every other question surface in Lomi answers you immediately, and should. An
 * exam must not: the score is the point, and a student who learns the answer to
 * Q3 mid-paper is no longer sitting the paper. So answers stay provisional and
 * changeable until the section closes, nothing is revealed until the end, and
 * then EVERYTHING is — score, per-section split, every item with its
 * explanation, Amharic and Ask Lomi. The reveal is not withheld, it is
 * postponed to where it teaches instead of leaks.
 *
 * ## Where the state lives
 *
 * The run is held in localStorage under `ps_sitting:<student>:<pkg>`, keyed to
 * ABSOLUTE deadlines rather than remaining seconds. That is deliberate: a
 * refresh must not hand back a fresh paper with a fresh clock, and a student
 * who closes the tab for ten minutes must lose those ten minutes, because that
 * is what a clock is. It also means the clock is editable by anyone who opens
 * devtools — accepted, since this is a self-scored readiness check and the only
 * person a cheated clock misleads is the student holding it. If a sitting ever
 * gates anything, the deadline moves server-side.
 *
 * Answers are NOT held hostage to the end: each section flushes to
 * /past-exams/session/* as it closes, so a completed section counts towards
 * mastery, spaced review, combos and rank even if the student abandons the
 * paper in the next one.
 */

type Item = {
  question_id: string;
  question: string;
  options: Array<{ label: string; text: string }>;
  correct_answer: string;
  explanation: string;
  distractor_notes?: Record<string, string>;
  topic: string | null;
  category: string | null;
  /** The stimulus this question is asked about. When present, `question` is
   *  the SHORT stem with the passage already removed — see `PackageItem`. */
  passage?: string | null;
  /** Stable across every question the stimulus serves, so the panel can stay
   *  open across a group of five and reset on the sixth. */
  passage_id?: string | null;
  passage_kind?: 'passage' | 'scenario' | null;
  figure?: { svg: string; alt?: string } | null;
};

type Section = {
  key: string;
  label: string;
  count: number;
  minutes: number;
  blueprint_items: number;
  blueprint_minutes: number;
  pool: number;
  /** Verbal 35 / Analytical 20 on an authored VA paper — for the report only.
   *  NOT clocks: a mock is ONE clock and the student may work back and forth
   *  across the whole paper. Absent on the pool-sampled check. */
  parts?: Array<{ label: string; count: number }>;
  items: Item[];
};

/** Either an authored paper or the pool-sampled quick check. The runner is the
 *  same for both; only where the questions come from differs. */
type Paper = Awaited<ReturnType<typeof packagesAPI.sitting>> | MockPaper;

type Answer = { label: string; seconds: number };

type RunState = {
  v: 1;
  mode: 'readiness' | 'full' | 'mock';
  paper: Paper;
  answers: Record<string, Answer>;
  flags: string[];
  /** Which section is open. Equals paper.sections.length once all have closed. */
  sectionIdx: number;
  /** Absolute epoch-ms deadline of the OPEN section; null while between sections. */
  deadline: number | null;
  /** Sections already closed, in order, with why and how long they took. */
  closed: Array<{ key: string; reason: 'clock' | 'submitted'; seconds: number }>;
  /** When the OPEN section was opened — so a section's real duration is known
   *  even when it was submitted early. Pace is the thing the sitting teaches;
   *  not measuring it would be odd. */
  sectionOpenedAt: number | null;
  /** The practice session answers are being flushed into. */
  sessionId: string | null;
  startedAt: number;
};

/** The sitting's own accent — deliberately not `mock` purple. A student should
 *  never confuse the weekly mock with the entrance paper. */
const SITTING_ACCENT: ExamAccent = {
  // Lomi green. This was amber, chosen to separate it from the weekly mock's
  // purple — which also separated it from Lomi, and a page of the product
  // should look like the product. Separation now comes from the clock and the
  // part-lock, which is what actually makes this surface different.
  accent: '#4a7c10', soft: '#f4f9ec', ring: '#d6e9b8', ink: '#3d6a0c',
  dark: {
    accent: '#86c232', soft: 'rgba(134, 194, 50, 0.16)',
    ring: 'rgba(134, 194, 50, 0.4)', ink: '#a5d65a',
  },
};

const clock = (s: number) => {
  const t = Math.max(0, s);
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

/** Per PAPER, not per package. One key for the whole package meant starting
 *  the Verbal paper silently overwrote a half-finished Quantitative one — and
 *  restored the wrong paper's answers into it. */
const storeKey = (pkg: string, form?: string) =>
  `ps_sitting:${localStorage.getItem('student_id') || 'anon'}:${pkg}${form ? `:${form}` : ''}`;

const load = (pkg: string, form?: string): RunState | null => {
  try {
    const raw = localStorage.getItem(storeKey(pkg, form));
    if (!raw) return null;
    const s = JSON.parse(raw) as RunState;
    if (s?.v !== 1 || !s.paper?.sections?.length) return null;
    // A run whose last part has closed is FINISHED. Restoring it left the
    // student staring at a "See your results" button as the default state of
    // the tab, forever, instead of their mock history.
    if (s.sectionIdx >= s.paper.sections.length) return null;
    return s;
  } catch {
    return null;
  }
};

const save = (pkg: string, s: RunState | null, form?: string) => {
  try {
    if (s) localStorage.setItem(storeKey(pkg, form), JSON.stringify(s));
    else localStorage.removeItem(storeKey(pkg, form));
  } catch {
    /* A full quota must not end the sitting — the run survives in memory. */
  }
};

/* ------------------------------------------------------------------ door -- */

type Attempt = Awaited<ReturnType<typeof packagesAPI.mocks>>['attempts'][number];

/** "3 Sept · 12/16 · 75%" — an attempt in one line. */
const AttemptRow: React.FC<{ a: Attempt }> = ({ a }) => {
  const when = a.started_at
    ? new Date(a.started_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    : '—';
  return (
    <li className="pst-attempt" data-finished={a.finished || undefined}>
      <span className="pst-attwhen">{when}</span>
      <span className="pst-attscore">
        <b>{a.correct}</b>
        <span>/{a.total}</span>
      </span>
      <span className="pst-attbar">
        <i style={{ width: `${a.score_pct ?? 0}%` }} />
      </span>
      <span className="pst-attpct">
        {a.score_pct !== null ? `${a.score_pct}%` : '—'}
        {!a.finished && <em className="pst-attleft"> · left early</em>}
      </span>
    </li>
  );
};

const Door: React.FC<{
  paper: Paper;
  attempts: Attempt[] | null;
  onStart: (full: boolean) => void;
  busy: boolean;
}> = ({ paper, attempts, onStart, busy }) => {
  // "Full" is only a real choice when the pool can actually make it longer,
  // and never on an authored paper — that paper IS its length.
  const authored = paper.mode === 'mock';
  const fullDiffers = !authored && paper.ceiling > paper.total;

  if (!paper.sections.length)
    return (
      <div className="pst-door">
        <h2 className="pst-h">No mock available yet</h2>
        <p className="ps-muted">
          A mock needs questions tagged to the paper&rsquo;s parts. None are
          published for this package yet.
        </p>
      </div>
    );

  return (
    <div className="pst-door">
      {/* Lead with what they are about to do, not with a caveat about it. The
          honest line still appears — it moves below the sections, where it
          qualifies a paper the student can already see the shape of, instead
          of apologising for one they cannot. */}
      <h2 className="pst-h">
        {authored
          ? `${paper.label} · Form ${paper.letter}`
          : paper.is_representative
            ? 'Full mock exam'
            : 'Quick check'}
      </h2>
      <p className="ps-muted">
        {paper.total} questions · {paper.minutes} min.{' '}
        {authored
          ? // One clock, because the blueprint says so: the 65 minutes is the
            // assessment timer and the 40/25 split is pacing guidance, "not
            // enforced section cutoffs". Saying it here is what stops a
            // student rationing time against a cutoff that does not exist.
            'One clock for the whole paper — you can move back and forth.'
          : 'Each part closes on its own clock.'}
      </p>

      <ol className="pst-sections">
        {paper.sections.map((s, i) => (
          <li key={s.key} className="pst-secrow">
            <span className="pst-secnum">{i + 1}</span>
            <span className="pst-secname">{s.label}</span>
            <span className="pst-secmeta">
              {s.parts && s.parts.length > 1
                ? s.parts.map((x) => `${x.count} ${x.label.split(' ')[0].toLowerCase()}`).join(' + ')
                : `${s.count} question${s.count === 1 ? '' : 's'}`}{' '}
              · {s.minutes} min
              {s.count < s.blueprint_items && (
                <em className="pst-scaled">
                  {' '}
                  (paper: {s.blueprint_items} in {s.blueprint_minutes})
                </em>
              )}
            </span>
          </li>
        ))}
      </ol>

      {!paper.is_representative && (
        // Kept, but placed after the sections: it now qualifies a paper the
        // student can already see the shape of, rather than being the first
        // thing they read. A score off a thin bank is an estimate of an
        // estimate, and saying so is the difference between a useful signal
        // and a misleading one — but saying it first reads as an apology.
        <p className="pst-honest">
          {paper.ceiling} questions in the bank so far — practice for the
          format, not a predicted score.
        </p>
      )}

      <div className="pst-startrow">
        <button
          className="pst-start"
          onClick={() => onStart(false)}
          disabled={busy}
        >
          {authored ? 'Start the paper' : 'Start the check'} · {paper.total}{' '}
          questions · {paper.minutes} min
        </button>
        {fullDiffers && (
          <button className="ps-btn ghost" onClick={() => onStart(true)} disabled={busy}>
            Take the longer version instead
          </button>
        )}
      </div>
    </div>
  );
};

/* ---------------------------------------------------------- interstitial -- */

const Between: React.FC<{
  justClosed: { section: Section; reason: 'clock' | 'submitted'; answered: number } | null;
  next: Section | null;
  /** Which section this is, out of how many — a student mid-paper needs to
   *  know how much paper is left, and "Next part" alone does not say. */
  step: number;
  total: number;
  onOpen: () => void;
  onFinish: () => void;
}> = ({ justClosed, next, step, total, onOpen, onFinish }) => (
  // Its own centred page. It used to inherit the runner's full-bleed
  // `padding: 0` and rendered jammed into the top-left corner of an empty
  // screen, which read as a broken page at the one moment the student is
  // between two clocks and least able to tell a pause from a crash.
  <div className="pst-stage">
    <div className="pst-door pst-between">
      <div className="pst-steps" aria-label={`Part ${step} of ${total}`}>
        {Array.from({ length: total }, (_, i) => (
          <span key={i} className="pst-step" data-state={i < step - 1 ? 'done' : i === step - 1 ? 'now' : undefined} />
        ))}
        <span className="pst-stepnum">
          {next ? `Part ${step} of ${total}` : 'Mock complete'}
        </span>
      </div>

      {justClosed && (
        <>
          <h2 className="pst-h">{justClosed.section.label} is over</h2>
          <p className="ps-muted">
            {justClosed.reason === 'clock' ? 'Time ran out.' : 'You finished it.'}{' '}
            You answered {justClosed.answered} of {justClosed.section.count}.
            {justClosed.reason === 'clock' &&
              justClosed.answered < justClosed.section.count &&
              ' In the real paper, unanswered is unanswered — pace is a skill you practise here, not on the day.'}
          </p>
        </>
      )}

      {next ? (
        <>
          <div className="pst-nextcard">
            <div className="pst-nextlabel">Next part</div>
            <div className="pst-nextname">{next.label}</div>
            <div className="pst-nextmeta">
              {next.count} questions · {next.minutes} minutes
            </div>
          </div>
          <p className="ps-muted pst-small">
            The clock starts when you open it, not now — take a breath first.
          </p>
          <button className="pst-start" onClick={onOpen}>
            Start {next.label} →
          </button>
        </>
      ) : (
        <button className="pst-start" onClick={onFinish}>
          See your results →
        </button>
      )}
    </div>
  </div>
);

/* ---------------------------------------------------------------- review -- */

const Review: React.FC<{
  paper: Paper;
  answers: Record<string, Answer>;
  flags: string[];
  closed: RunState['closed'];
  victory: Victory | null;
  onAskLomi?: (q: any) => void;
  onAgain: () => void;
  onLeave: () => void;
}> = ({ paper, answers, flags, closed, victory, onAskLomi, onAgain, onLeave }) => {
  const [open, setOpen] = useState<string | null>(null);
  // A student who got 11 of 16 wants the 5, not a scroll through the 11. The
  // default is still everything — "wrong only" is a filter, not the frame,
  // because a review that only ever shows failures teaches a student to dread
  // opening it.
  const [only, setOnly] = useState<'all' | 'wrong' | 'flagged'>('all');
  const flagged = new Set(flags);

  const score = (items: Item[]) =>
    items.reduce(
      (acc, it) => {
        const a = answers[it.question_id];
        if (a) acc.answered += 1;
        if (a && a.label === it.correct_answer) acc.correct += 1;
        return acc;
      },
      { correct: 0, answered: 0 }
    );

  const all = paper.sections.flatMap((s) => s.items);
  const overall = score(all);
  const pct = all.length ? Math.round((overall.correct / all.length) * 100) : 0;
  const totalSecs = closed.reduce((a, c) => a + c.seconds, 0);
  const ranAnyOut = closed.some((c) => c.reason === 'clock');
  const wrongCount = all.filter(
    (i) => answers[i.question_id] && answers[i.question_id].label !== i.correct_answer
  ).length;
  const blankCount = all.filter((i) => !answers[i.question_id]).length;

  const visible = all.filter((it) => {
    if (only === 'wrong')
      return !answers[it.question_id] || answers[it.question_id].label !== it.correct_answer;
    if (only === 'flagged') return flagged.has(it.question_id);
    return true;
  });

  return (
    <div className="pst-review">
      <div className="pst-scorecard">
        <div className="pst-scorenum">
          {overall.correct}
          <span className="pst-scoreof"> / {all.length}</span>
        </div>
        <div className="pst-scorepct">
          {pct}% ·{' '}
          {/* Rounding to minutes turns a fast run into "0 min of 27", which
              reads as a bug rather than as speed. */}
          {totalSecs < 90
            ? `${totalSecs}s`
            : `${Math.round(totalSecs / 60)} min`}{' '}
          of {paper.minutes} min
          {blankCount > 0 && ` · ${blankCount} unanswered`}
        </div>
        {!paper.is_representative && (
          <p className="pst-honest pst-honest--tight">
            Off {all.length} questions, not 100 — a read on your pace and your
            handling of the section lock, not a predicted score.
          </p>
        )}
      </div>

      {/* What the paper earned. These numbers already existed for every other
          surface in Lomi; a 27-minute timed paper showing none of them made
          the sitting feel like it counted for less than three idle questions
          in the practice room. */}
      {victory && (
        <div className="pst-earned" data-testid="sitting-earned">
          <div className="pst-earn">
            <b>+{victory.xp_earned}</b>
            <span>XP</span>
          </div>
          {victory.combo.session_max > 0 && (
            <div className="pst-earn">
              <b>{victory.combo.session_max}</b>
              <span>best run{victory.combo.is_personal_best ? ' · PB' : ''}</span>
            </div>
          )}
          {victory.streak.alive && victory.streak.current > 0 && (
            <div className="pst-earn">
              <b>{victory.streak.current}</b>
              <span>day streak</span>
            </div>
          )}
          {victory.rank && victory.rank_before &&
            victory.rank_before.rank > victory.rank.rank && (
              <div className="pst-earn pst-earn--move">
                <b>
                  #{victory.rank_before.rank} → #{victory.rank.rank}
                </b>
                <span>
                  you passed {victory.rank_before.rank - victory.rank.rank} students
                </span>
              </div>
            )}
          {victory.personal_bests.map((pb) => (
            <div key={pb.kind} className="pst-earn pst-earn--pb">
              <b>🏅 {pb.label}</b>
              <span>
                {pb.old} → {pb.new} {pb.unit}
              </span>
            </div>
          ))}
        </div>
      )}

      {ranAnyOut && blankCount > 0 && (
        // The single most useful thing the sitting can tell a student, and it
        // is about pace rather than knowledge — which is exactly the lesson
        // the section lock exists to deliver.
        <p className="pst-pace">
          You ran out of time with <b>{blankCount}</b> question
          {blankCount === 1 ? '' : 's'} unanswered. On the real paper those
          score zero however well you know them — worth a faster first pass,
          flagging what you want to return to.
        </p>
      )}

      <div className="pst-bysection">
        {paper.sections.map((s) => {
          const sc = score(s.items);
          return (
            <div key={s.key} className="pst-secscore">
              <span className="pst-secname">{s.label}</span>
              <span className="pst-secbar">
                <i
                  style={{
                    width: `${s.items.length ? (sc.correct / s.items.length) * 100 : 0}%`,
                  }}
                />
              </span>
              <span className="pst-secmeta">
                {sc.correct}/{s.items.length}
                {sc.answered < s.items.length && (
                  <em className="pst-scaled"> · {s.items.length - sc.answered} unanswered</em>
                )}
              </span>
            </div>
          );
        })}
      </div>

      <div className="pst-revbar">
        <h3 className="pst-revhead">
          {only === 'all' ? 'Every question' : only === 'wrong' ? 'What to fix' : 'Flagged'}
        </h3>
        <div className="pst-revfilter" role="group" aria-label="Filter review">
          <button className={only === 'all' ? 'on' : ''} onClick={() => setOnly('all')}>
            All {all.length}
          </button>
          <button
            className={only === 'wrong' ? 'on' : ''}
            onClick={() => {
              setOnly('wrong');
              track('package.sitting.review.filter', { filter: 'wrong' });
            }}
            disabled={wrongCount + blankCount === 0}
          >
            To fix {wrongCount + blankCount}
          </button>
          <button
            className={only === 'flagged' ? 'on' : ''}
            onClick={() => {
              setOnly('flagged');
              track('package.sitting.review.filter', { filter: 'flagged' });
            }}
            disabled={flags.length === 0}
          >
            Flagged {flags.length}
          </button>
        </div>
      </div>
      <div className="pst-revlist">
        {visible.length === 0 && (
          <p className="ps-muted">Nothing here — everything in this group is right.</p>
        )}
        {visible.map((it) => {
          const n = all.indexOf(it);
          const a = answers[it.question_id];
          const ok = a && a.label === it.correct_answer;
          const state = !a ? 'blank' : ok ? 'right' : 'wrong';
          const isOpen = open === it.question_id;
          return (
            <div key={it.question_id} className="pst-rev" data-state={state}>
              <button
                className="pst-revtop"
                onClick={() => setOpen(isOpen ? null : it.question_id)}
                aria-expanded={isOpen}
              >
                <span className="pst-revn">{n + 1}</span>
                <span className="pst-revmark">
                  {state === 'right' ? '✓' : state === 'wrong' ? '✕' : '–'}
                </span>
                <span className="pst-revq">
                  {/* Plain text, not markdown: a raw slice cuts mid-emphasis
                      and leaves a dangling `**` on screen. */}
                  {plain(it.question).length > 110
                    ? `${plain(it.question).slice(0, 110)}…`
                    : plain(it.question)}
                </span>
                {flagged.has(it.question_id) && <span className="pst-revflag">🚩</span>}
              </button>

              {isOpen && (
                <div className="pst-revbody">
                  <div className="pst-revfull">
                    <MarkdownRenderer>{it.question}</MarkdownRenderer>
                  </div>
                  <div className="pst-revopts">
                    {it.options.map((o) => (
                      <div
                        key={o.label}
                        className="pst-revopt"
                        data-role={
                          o.label === it.correct_answer
                            ? 'correct'
                            : a && o.label === a.label
                            ? 'chosen'
                            : undefined
                        }
                      >
                        <span className="pst-revlab">{o.label}</span>
                        <MarkdownRenderer inline>{o.text}</MarkdownRenderer>
                      </div>
                    ))}
                  </div>
                  {!a && (
                    <p className="ps-muted pst-small">
                      You did not answer this one — the section closed first.
                    </p>
                  )}
                  {it.explanation && (
                    <div className="pst-revexpl">
                      <MarkdownRenderer>{it.explanation}</MarkdownRenderer>
                    </div>
                  )}
                  {a && !ok && it.distractor_notes?.[a.label] && (
                    <div className="pst-revwhy">
                      <MarkdownRenderer>{it.distractor_notes[a.label]}</MarkdownRenderer>
                    </div>
                  )}
                  <div className="ps-blockbar">
                    <TranslateAmharicButton
                      text={it.explanation || ''}
                      surface="package_sitting_explanation"
                      cacheKey={`${it.question_id}:expl`}
                      compact
                    />
                    {onAskLomi && (
                      <button className="ps-chip" onClick={() => onAskLomi(it)}>
                        💬 Ask Lomi
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="pst-revfoot">
        <button className="pst-start" onClick={onAgain}>
          Take it again
        </button>
        <button className="ps-btn ghost" onClick={onLeave}>
          Back to the package
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ main -- */

const PackageSitting: React.FC<{
  pkg: string;
  /** Which AUTHORED paper to sit — "QUANT_MOCK_A", "VA_MOCK_B". When absent
   *  the runner serves the pool-sampled quick check instead, which is the
   *  behaviour every caller had before the mock room existed. */
  form?: string;
  onAskLomi?: (q: any) => void;
  onLeave: () => void;
  /** Told when a run starts and ends, so the shell can hide its chrome — an
   *  exam with a nav bar offering two other rooms is not an exam. */
  onRunning?: (running: boolean) => void;
}> = ({ pkg, form, onAskLomi, onLeave, onRunning }) => {
  const [paper, setPaper] = useState<Paper | null>(null);
  const [run, setRun] = useState<RunState | null>(() => load(pkg, form));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [idx, setIdx] = useState(0);
  const [done, setDone] = useState(false);
  const [victory, setVictory] = useState<Victory | null>(null);
  const [attempts, setAttempts] = useState<Attempt[] | null>(null);

  const sections = (run?.paper.sections || []) as Section[];
  const section: Section | null =
    run && run.sectionIdx < sections.length ? sections[run.sectionIdx] : null;
  const running = !!section && run?.deadline != null && !done;

  useEffect(() => {
    onRunning?.(!!run && !done);
  }, [run, done, onRunning]);

  /* ---- the door's paper (a preview; the run gets its own fetch) ---- */
  useEffect(() => {
    if (run) return;
    let dead = false;
    (async () => {
      try {
        // History alongside the paper: a returning student's first question is
        // "how did I do last time", not "what are the rules".
        const [p, m] = await Promise.all([
          form ? packagesAPI.mockPaper(pkg, form) : packagesAPI.sitting(pkg, false),
          packagesAPI.mocks(pkg).catch(() => ({ attempts: [] as Attempt[] })),
        ]);
        if (!dead) setAttempts(m.attempts);
        if (!dead) setPaper(p);
      } catch (e: any) {
        if (!dead) setErr(e?.response?.data?.detail || 'Mock exam not available.');
      }
    })();
    return () => {
      dead = true;
    };
  }, [pkg, form, run]);

  /* ---- persistence ---- */
  useEffect(() => {
    save(pkg, run, form);
  }, [pkg, form, run]);

  /* ---- the clock ---- */
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [running]);

  const remaining = run?.deadline ? Math.ceil((run.deadline - now) / 1000) : 0;

  /**
   * Push a closed section's answers into the practice machinery.
   *
   * Deliberately at section close rather than per answer: in an exam an answer
   * is provisional until the section shuts, and recording each tap would
   * write — and score — the answers a student then changed their mind about.
   *
   * The first flush opens the session, later ones extend it, so one paper is
   * one session and the consecutive-correct combo runs the length of it.
   * Everything is wrapped: `/past-exams/session/start` abandons any other
   * in-progress session for the same subject, so a student who opened the
   * Questions room in another tab can legitimately have this fail. The results
   * screen is computed locally and does not depend on any of it — a lost flush
   * costs a statistic, never the sitting.
   */
  const flush = useCallback(
    async (state: RunState, sec: Section): Promise<string | null> => {
      const studentId = localStorage.getItem('student_id');
      const subject = state.paper.subjects?.[0];
      if (!studentId || !subject) return state.sessionId;

      const ids = sec.items.map((i) => i.question_id);
      let sessionId = state.sessionId;
      try {
        if (!sessionId) {
          const started = await pastExamAPI.startSession({
            student_id: studentId,
            subject,
            question_ids: ids,
            // Otherwise a timed paper is indistinguishable in the database
            // from someone idly answering three questions, and completion,
            // abandonment and pace for this surface cannot be queried at all.
            origin: 'mock',
            // WHICH paper. Without it the mock room cannot tell a student who
            // has sat Form A from one who has sat nothing, so it cannot open
            // Form B as the re-test or put a score on the card.
            form_code: state.paper.mode === 'mock' ? state.paper.form_code : undefined,
          });
          sessionId = started?.session_id || started?.id || null;
        } else {
          await pastExamAPI.extendSession(sessionId, {
            student_id: studentId,
            question_ids: ids,
          });
        }
        if (!sessionId) return null;

        for (const it of sec.items) {
          const a = state.answers[it.question_id];
          // Unanswered items are not recorded as wrong. A blank is a pacing
          // failure, not a knowledge gap, and feeding it to spaced review would
          // schedule a student to re-study something they may know perfectly
          // well and simply never reached.
          if (!a) continue;
          await pastExamAPI.saveAnswer(sessionId, {
            student_id: studentId,
            question_id: it.question_id,
            selected_answer: a.label,
            is_correct: a.label === it.correct_answer,
            time_spent_seconds: a.seconds,
            topic: it.topic || undefined,
          });
        }
      } catch {
        /* see the docstring — never fatal */
      }
      return sessionId;
    },
    []
  );

  /* ---- transitions ---- */

  const closeSection = useCallback(
    async (reason: 'clock' | 'submitted') => {
      if (!run || !section) return;
      const answered = section.items.filter((i) => run.answers[i.question_id]).length;
      const seconds = run.sectionOpenedAt
        ? Math.max(0, Math.round((Date.now() - run.sectionOpenedAt) / 1000))
        : 0;
      track('package.sitting.section.close', {
        package: pkg, section: section.key, reason,
        answered, total: section.count, mode: run.mode,
        seconds,
        // Pace is what the section lock actually teaches, so it is measured
        // rather than inferred: a student who answers everything with time to
        // spare and one who runs out are the two cases we most want to tell
        // apart, and "answered N of M" alone cannot.
        seconds_per_item: answered ? Math.round(seconds / answered) : null,
        flagged: run.flags.filter((f) =>
          section.items.some((i) => i.question_id === f)
        ).length,
      });
      const sessionId = await flush(run, section);
      setRun((s) =>
        s
          ? {
              ...s,
              sessionId,
              deadline: null,
              sectionOpenedAt: null,
              sectionIdx: s.sectionIdx + 1,
              closed: [...s.closed, { key: section.key, reason, seconds }],
            }
          : s
      );
      setIdx(0);
    },
    [run, section, pkg, flush]
  );

  // The lock itself. Everything else on this page is presentation; this is the
  // behaviour the sitting exists to reproduce.
  useEffect(() => {
    if (!running || remaining > 0) return;
    closeSection('clock');
  }, [running, remaining, closeSection]);

  const start = async (full: boolean) => {
    setBusy(true);
    try {
      // Fetched fresh rather than reusing the door's copy: the door may have
      // been open for a while, and the quick check is drawn at random. An
      // authored form is the same items either way, and is re-fetched only so
      // both paths have one shape.
      const p = form
        ? await packagesAPI.mockPaper(pkg, form)
        : await packagesAPI.sitting(pkg, full);
      if (!p.sections.length) {
        setErr('No questions are available for a mock yet.');
        return;
      }
      track('package.sitting.start', {
        package: pkg, mode: p.mode, total: p.total,
        minutes: p.minutes, representative: p.is_representative,
      });
      setNow(Date.now());
      setRun({
        v: 1,
        mode: p.mode,
        paper: p,
        answers: {},
        flags: [],
        sectionIdx: 0,
        // The first section opens on the Start button. Pressing "Start · 16
        // questions · 27 min" already IS the consent to begin, and making the
        // student then confirm an interstitial that has nothing to report is
        // a second door in front of the first. Later sections get the
        // interstitial because it has something to say: what just closed.
        deadline: Date.now() + (p.sections[0].minutes * 60_000),
        sectionOpenedAt: Date.now(),
        closed: [],
        sessionId: null,
        startedAt: Date.now(),
      });
      setIdx(0);
      setDone(false);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Could not start the mock.');
    } finally {
      setBusy(false);
    }
  };

  const openSection = () => {
    if (!run || !section) return;
    track('package.sitting.section.open', {
      package: pkg, section: section.key, minutes: section.minutes,
    });
    setNow(Date.now());
    setRun({
      ...run,
      deadline: Date.now() + section.minutes * 60_000,
      sectionOpenedAt: Date.now(),
    });
    setIdx(0);
  };

  const finish = async () => {
    if (!run) return;
    const all = run.paper.sections.flatMap((s) => s.items);
    const correct = all.filter(
      (i) => run.answers[i.question_id]?.label === i.correct_answer
    ).length;
    track('package.sitting.complete', {
      package: pkg, mode: run.mode, total: all.length, correct,
      answered: Object.keys(run.answers).length,
      representative: run.paper.is_representative,
      seconds: Math.round((Date.now() - run.startedAt) / 1000),
    });
    if (run.sessionId) {
      const studentId = localStorage.getItem('student_id');
      try {
        if (studentId) await pastExamAPI.completeSession(run.sessionId, studentId);
      } catch {
        /* the session stays in_progress; harmless */
      }
      // What the paper EARNED — XP, rank movement, combo, streak. This
      // endpoint already existed and was already rich; it simply had no caller
      // here, so a student could sit a timed paper and be told nothing about
      // what it did for them, while answering three questions casually in the
      // Questions room showed the lot. Fetched after complete() so the
      // session's events are all in.
      try {
        setVictory(await gamificationAPI.sessionSummary(run.sessionId));
      } catch {
        // Never blocks the results — the score is computed locally and is the
        // thing the student actually came back for.
      }
    }
    setDone(true);
  };

  /* ---- answering ---- */

  const shownAt = useRef(Date.now());
  useEffect(() => {
    shownAt.current = Date.now();
  }, [idx, run?.sectionIdx]);

  const choose = (label: string) => {
    if (!run || !section) return;
    const it = section.items[idx];
    if (!it) return;
    const prev = run.answers[it.question_id];
    setRun({
      ...run,
      answers: {
        ...run.answers,
        [it.question_id]: {
          label,
          // Time accumulates across visits — a student who comes back to a
          // flagged question spent that time on it too.
          seconds:
            (prev?.seconds || 0) +
            Math.max(0, Math.round((Date.now() - shownAt.current) / 1000)),
        },
      },
    });
    shownAt.current = Date.now();
  };

  const toggleFlag = () => {
    if (!run || !section) return;
    const qid = section.items[idx]?.question_id;
    if (!qid) return;
    setRun({
      ...run,
      flags: run.flags.includes(qid)
        ? run.flags.filter((f) => f !== qid)
        : [...run.flags, qid],
    });
  };

  const abandon = () => {
    if (!run) return;
    track('package.sitting.abandon', {
      package: pkg, section: section?.key || null,
      answered: Object.keys(run.answers).length,
    });
    setRun(null);
    setDone(false);
    onLeave();
  };

  /* ---- views ---- */

  const justClosed = useMemo(() => {
    if (!run || !run.closed.length) return null;
    const last = run.closed[run.closed.length - 1];
    const sec = sections.find((s) => s.key === last.key);
    if (!sec) return null;
    return {
      section: sec,
      reason: last.reason,
      answered: sec.items.filter((i) => run.answers[i.question_id]).length,
    };
  }, [run, sections]);

  if (err && !run)
    return (
      <div className="pst-door">
        <p className="ps-muted">{err}</p>
        <button className="ps-btn ghost" onClick={onLeave}>
          Back to the package
        </button>
      </div>
    );

  if (!run) {
    if (!paper) return <div className="ps-centre"><div className="ps-spin" /></div>;
    return <Door paper={paper} attempts={attempts} onStart={start} busy={busy} />;
  }

  if (done)
    return (
      <Review
        paper={run.paper}
        answers={run.answers}
        flags={run.flags}
        closed={run.closed}
        victory={victory}
        onAskLomi={onAskLomi}
        onAgain={() => {
          setRun(null);
          setDone(false);
          setVictory(null);
          setPaper(null);
          setAttempts(null);
        }}
        onLeave={() => {
          setRun(null);
          onLeave();
        }}
      />
    );

  // Between sections — either the paper is finished, or the next one is waiting
  // for the student to open it. The clock does not start on an interstitial.
  if (!section || run.deadline == null)
    return (
      <Between
        justClosed={justClosed}
        next={section}
        step={run.sectionIdx + 1}
        total={sections.length}
        onOpen={openSection}
        onFinish={finish}
      />
    );

  const it = section.items[idx];
  if (!it) return null;
  const answeredCount = section.items.filter((i) => run.answers[i.question_id]).length;
  const low = remaining <= 60;
  // "Passage · question 2 of 5". Grouped by matching passage TEXT, which is
  // how PastESSLCEQuiz already does it — the passage is the identity, so no
  // extra id is needed and none is stored.
  const passageGroup = (() => {
    if (!it.passage) return null;
    const group = section.items.filter((q) => q.passage === it.passage);
    if (group.length < 2) return null;
    return { index: group.indexOf(it) + 1, total: group.length };
  })();

  return (
    <div className="pst-run">
      <QuizCanvas
        accent={SITTING_ACCENT}
        examMode
        onBack={abandon}
        answered={answeredCount}
        qNumber={idx + 1}
        total={section.items.length}
        contextLabel={`${section.label}${it.topic ? ` · ${it.topic}` : ''}`}
        rightSlot={
          <span
            className={`pst-clock${low ? ' is-low' : ''}`}
            data-testid="sitting-timer"
            aria-live={low ? 'polite' : 'off'}
          >
            ⏱ {clock(remaining)}
          </span>
        }
        questionText={it.question}
        stimulus={
          // The shared panel, not a mock-specific one. It opens EXPANDED and
          // caps its height with an internal scroll rather than collapsing by
          // default — a decision made against the content in d4de7f26 (median
          // UAT passage is ~152 characters, and for Analytical Reasoning the
          // passage IS the rules). The collapse choice sticks across the group,
          // which is what actually helps by question three of five.
          it.passage || it.figure ? (
            <>
              <PassagePanel
                text={it.passage}
                cacheKey={`mock:${it.question_id}`}
                surface="package_sitting"
                position={passageGroup}
                label={it.passage_kind === 'scenario' ? 'Scenario' : undefined}
                trackProps={{ question_id: it.question_id }}
              />
              {it.figure?.svg && <ContentFigure figure={it.figure} />}
            </>
          ) : undefined
        }
        options={it.options}
        selected={run.answers[it.question_id]?.label ?? null}
        lastAnswer={null}
        submitting={false}
        onSelect={choose}
        onPrev={idx > 0 ? () => setIdx(idx - 1) : undefined}
        flagged={run.flags.includes(it.question_id)}
        onFlag={toggleFlag}
        onNext={() =>
          idx < section.items.length - 1 ? setIdx(idx + 1) : closeSection('submitted')
        }
        isLast={idx === section.items.length - 1}
        nextLabel="Next →"
        finishLabel={
          run.sectionIdx === sections.length - 1 ? 'Finish the mock' : 'Finish this part'
        }
        questionId={it.question_id}
        optionTestId={(l) => `sitting-option-${l}`}
        runTestId="sitting-run"
      />

      {/* A jump strip, because "flag it and come back" is only real if coming
          back is one tap rather than a hunt. */}
      <div className="pst-jump" aria-label="Questions in this section">
        {section.items.map((q, n) => (
          <button
            key={q.question_id}
            className="pst-jumpdot"
            data-state={
              run.answers[q.question_id] ? 'done' : undefined
            }
            data-flag={run.flags.includes(q.question_id) || undefined}
            data-on={n === idx || undefined}
            onClick={() => setIdx(n)}
            aria-label={`Question ${n + 1}${run.answers[q.question_id] ? ', answered' : ''}`}
          >
            {n + 1}
          </button>
        ))}
        <button className="pst-submit" onClick={() => closeSection('submitted')}>
          Finish {section.label}
        </button>
      </div>
    </div>
  );
};

export default PackageSitting;
